import { TFetchUserParam } from '../typings';
import { IFullGroupUser, IGroupUser } from '../../../../typings/model/user';
import { getFormatUserListMap } from '../util';
import { isGroupAdmin } from '../../../group';
import UserApi from '../../../api/user';
import { UserManager } from '../../user';
import { BaseGroupUserManager } from '../base';
import { PAGE_SIZE } from '../constant';
import { RoleManager, RoleManagerUpdateType } from '../../role';
import { getGroupChannelKey } from '../../util';
import { COMMON_CHANNEL_ID } from '../../../../constant/channel';

/**
 * 通用群用户信息管理中心
 * - 全局存储和分发相关的群用户信息
 */
class GroupUserManager extends BaseGroupUserManager {
  /** 单实例 */
  private static instance: GroupUserManager;
  public static getInstance(): GroupUserManager {
    if (!GroupUserManager.instance) {
      GroupUserManager.instance = new GroupUserManager();
    }

    return GroupUserManager.instance;
  }

  /**
   * 分页拉取用户数据的参数Map
   * - key: groupId + channelId, value: TFetchUserParam
   */
  private fetchUserParamMap: Record<string, TFetchUserParam> = {};

  /**
   * 更新用户信息
   * @param userIdList 用户id列表
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @returns 更新用户信息是否成功
   */
  public async updateUserInfo(
    userIdList: string[],
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): Promise<boolean> {
    const key = getGroupChannelKey(groupId, channelId);
    const response = await UserApi.getGrpUserInfoBatch(groupId, userIdList);
    const userList = Object.values(response?.mInfo || {});
    if (!userList.length) {
      return false;
    }
    const { baseUserList, groupUserList, dbUserList } = await getFormatUserListMap(groupId, key, userList);
    let formatGroupUserList: IGroupUser[] = [];
    for (const groupUser of groupUserList) {
      const newGroupUserList = groupUser.roleIdList.map(roleId => ({
        ...groupUser,
        roleId,
      }));
      formatGroupUserList = formatGroupUserList.concat(newGroupUserList);
    }
    this.updateCacheUser({
      groupId,
      channelId,
      addBaseUserList: baseUserList,
      addGroupUserList: formatGroupUserList,
      addDBUserList: dbUserList,
    });
    return true;
  }

  /**
   * 当前用户是否为指定群频道的管理员
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @return 是否为管理员
   */
  public isCurrentUserGroupChannelAdmin(groupId: string, channelId = COMMON_CHANNEL_ID): boolean {
    if (!groupId) return false;
    const userId = UserManager.getInstance().getCurrentUid();
    const user = this.getUserByGroupChannel(userId, groupId, channelId);
    const roleIdList = user?.roleIdList || [];
    return isGroupAdmin(roleIdList);
  }

  /**
   * 根据groupId + channelId + userId，获取指定的用户信息
   * @param userId 用户id
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @returns 用户信息
   */
  public getUserByGroupChannel(
    userId: string,
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): IFullGroupUser | null {
    const key = getGroupChannelKey(groupId, channelId);
    const baseUser = UserManager.getInstance().getUser(userId);
    const groupUser = this.userMapByGroupChannel.get(key)?.[userId];
    if (!baseUser || !groupUser) {
      return null;
    }

    return {
      ...baseUser,
      ...groupUser,
    };
  }

  /**
   * 拉取频道用户信息(做分页处理，分页参数GroupUserManager内部自动处理)
   * - 优先使用缓存数据
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @param needNotify 是否需要通知更新(默认：true)
   * @param needUpdate 有缓存数据时，是否需要同步更新(默认：true)
   * @returns 网络请求的页面状态
   */
  public async fetchChannelUserByPage(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
    needNotify = true,
    needUpdate = true,
  ): Promise<void> {
    const key = getGroupChannelKey(groupId, channelId);
    const cacheData = this.userSortListMapByGroupChannelRole.get(key);
    if (cacheData) {
      if (needNotify) {
        this.notifyUserUpdateEvent(groupId, channelId);
      }
      if (!needUpdate) {
        // 不用强制更新，则直接返回
        return;
      }
    }
    await this.fetchChannelUserByPageFromRemote(groupId, channelId);
  }

  /**
   * 从后台拉取频道用户数据(做分页处理，分页参数GroupUserManager内部自动处理)
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @param needFetchFromFirstPage 是否需要从第一页重新拉取(默认：否)
   * @returns 网络请求的页面状态
   */
  public async fetchChannelUserByPageFromRemote(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
    needFetchFromFirstPage = false,
  ): Promise<void> {
    const key = getGroupChannelKey(groupId, channelId);
    let pageId = '';
    let isEnd = false;
    if (!needFetchFromFirstPage) {
      const param = this.fetchUserParamMap[key];
      pageId = param?.pageId || '';
      isEnd = param?.isEnd || false;
    }
    if (isEnd) {
      return;
    }
    const data = await UserApi.getGrpUserList(PAGE_SIZE, groupId, channelId, pageId);
    const { baseUserList, groupUserList, dbUserList } = await getFormatUserListMap(groupId, key, data.userList || []);
    this.updateFetchUserParam(key, data.pid, data.isEnd);
    this.updateCacheUser({
      groupId,
      channelId,
      addBaseUserList: baseUserList,
      addGroupUserList: groupUserList,
      addDBUserList: dbUserList,
      needReset: needFetchFromFirstPage,
    });
  }

  /**
   * 获取当前分页拉取信息是否结束
   * @param groupId 群id
   * @param channelId 频道id
   * @returns 当前分页拉取信息是否结束
   */
  public getIsFetchChannelUserByPageEnd(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): boolean {
    const key = getGroupChannelKey(groupId, channelId);
    return this.fetchUserParamMap[key]?.isEnd || false;
  }

  /**
   * 插入群组用户
   * @param groupId
   * @param userList
   */
  public insertUserInfoByGroupId(groupId: string, userList: Array<IGroupUser>) {
    let hasChanged = false;
    const key = getGroupChannelKey(groupId);
    const userMapByGroupChannel = this.userMapByGroupChannel.get(key) || {};
    const userSortListMapByGroupChannelRole = this.userSortListMapByGroupChannelRole.get(key)
      || new Map<string, Array<IGroupUser>>();
    for (const user of userList) {
      const { uid, roleId } = user;
      const uidStr = String(uid);
      if (userMapByGroupChannel[uidStr]) return;
      hasChanged = true;

      // 1. 更新群组频道表中数据
      userMapByGroupChannel[uidStr] = user;

      // 2. 更新角色表
      const userSortList = userSortListMapByGroupChannelRole.get(roleId);
      if (userSortList) {
        if (!userSortList.some(item => String(item.uid) === uidStr)) {
          userSortList.push(user);
        }
      } else {
        userSortListMapByGroupChannelRole.set(roleId, [user]);
      }
    }
    this.userMapByGroupChannel.set(key, userMapByGroupChannel);
    this.userSortListMapByGroupChannelRole.set(key, userSortListMapByGroupChannelRole);
    if (hasChanged) {
      this.notifyUserUpdateEvent(groupId);
    }
  }

  /**
   * 删除群组用户
   * @param groupId
   * @param uidList
   */
  public removeUserInfoByGroupId(groupId: string, uidList: Array<string>) {
    const uidStrList = uidList.map(uid => String(uid));
    // 1. 删除当前群组下所有频道中该成员
    let hasChanged = false;
    for (const [key, obj] of this.userMapByGroupChannel) {
      if (key.startsWith(groupId)) {
        const tempObjSize = Object.keys(obj);
        uidStrList.forEach((uid) => {
          if (obj[uid]) {
            delete obj[uid];
          }
        });
        const finalObjSize = Object.keys(obj);
        if (finalObjSize < tempObjSize) {
          hasChanged = true;
        }
      }
    }
    // 2. 删除当前群组下角色组下
    for (const [key, userMapByRole] of this.userSortListMapByGroupChannelRole) {
      if (key.startsWith(groupId)) {
        for (const [, userList] of userMapByRole) {
          const fineIndex = userList.findIndex(user => uidStrList.indexOf(String(user.uid)) > -1);
          if (fineIndex > -1) {
            hasChanged = true;
            userList.splice(fineIndex, 1);
          }
        }
      }
    }
    if (hasChanged) {
      this.notifyUserUpdateEvent(groupId);
    }
  }

  /**
   * 根据群组id，更新用户角色信息
   * @param type 0:新增 1:删除
   * @param rid 角色ID
   * @param gid 群组ID
   * @param uidList uid列表
   */
  public async updateRoleInfoByGroupId({
    type, rid, gid, uidList,
  }: {
    type: string, rid: string, gid: string, uidList: Array<string>
  }) {
    const key = getGroupChannelKey(gid);
    const groupUserListMapByRole = this.userSortListMapByGroupChannelRole.get(key);
    if (!groupUserListMapByRole) return;
    let hasChanged = false;
    let roleName = '';
    if (type === '0') {
      const roleManager = RoleManager.getInstance();
      const updateType = RoleManagerUpdateType.updateImmediate;
      const roleMap = await roleManager.getRoleMapByGroupChannel(gid, COMMON_CHANNEL_ID, updateType);
      roleName = roleMap[rid]?.name ?? '';
    }
    uidList.forEach((uid) => {
      const groupUserList = groupUserListMapByRole.get(rid);
      const groupUserInfo = this.getUserByGroupChannel(uid, gid);
      if (groupUserInfo) {
        const index = groupUserInfo.roleIdList.indexOf(rid);
        // 1. 首先更新群组用户表
        if (type === '0') {
          if (index === -1) {
            hasChanged = true;
            groupUserInfo.roleIdList.push(rid);
          }
        } else if (type === '1') {
          if (index > -1) {
            hasChanged = true;
            groupUserInfo.roleIdList.splice(index, 1);
          }
        }
        // 2. 更新群组角色用户表
        if (type === '0') {
          const newGroupUserInfo = {
            ...groupUserInfo,
            roleName: roleName || groupUserInfo.roleName,
          };
          if (groupUserList) {
            const findIndex = groupUserList.findIndex(userInfo => String(userInfo.uid) === uid);
            if (findIndex === -1) {
              hasChanged = true;
              groupUserList.push(newGroupUserInfo);
            }
          } else {
            hasChanged = true;
            groupUserListMapByRole.set(rid, [newGroupUserInfo]);
          }
        } else if (type === '1') {
          if (groupUserList) {
            const findIndex = groupUserList.findIndex(userInfo => String(userInfo.uid) === uid);
            if (findIndex > -1) {
              hasChanged = true;
              groupUserList.splice(findIndex, 1);
            }
          }
        }
      }
    });
    if (hasChanged) {
      this.notifyUserUpdateEvent(gid);
    }
  }

  /**
   * 更新拉取分页拉取用户的参数
   * @param groupChannelKey 群组频道的key
   * @param pageId 页id
   * @param isEnd 是否拉取完毕
   */
  private updateFetchUserParam(groupChannelKey: string, pageId: string, isEnd: boolean) {
    this.fetchUserParamMap[groupChannelKey] = {
      pageId,
      isEnd,
    };
  }
}

export { GroupUserManager };
